import { useContext, useMemo } from "react";

import { swap } from "utils";

import { useNotify } from "data/messages/hooks";

import { useMutation, useMutationWithToaster } from "hooks";

import toGlobalId from "scenes/Hubly/components/Workspace/api/helper";
import { ToasterContext } from "scenes/Hubly/components/Workspace/Provider";

import {
  createFormField,
  deleteForm,
  updateForm,
  updateFormFieldSortOrder,
} from "./taskFormQueries";
import useFormFieldActions from "./useFormFieldActions";

export function useTaskForm(task, onUpdate) {
  const { fireError } = useContext(ToasterContext);
  const notifyNextWorkflows = useNotify("NEXT_WORKFLOWS");

  const [createFormFieldMutation, { loading: isCreatingFormField }] =
    useMutationWithToaster(createFormField, {
      onDone: (form) => {
        onUpdate({ ...task, form });
      },
    });

  const [updateFormMutation, { loading: isUpdatingForm }] =
    useMutationWithToaster(updateForm, {
      onDone: (form) => {
        onUpdate({ ...task, form });
        notifyNextWorkflows([true]);
      },
    });

  const [deleteFormMutation, { loading: isDeletingForm }] =
    useMutationWithToaster(deleteForm, {
      onDone: () => {
        onUpdate({ ...task, form: undefined });
        notifyNextWorkflows([true]);
      },
    });
  const updateFieldOrderMutation = useMutation(updateFormFieldSortOrder);

  const mapFieldsAction = useFormFieldActions({
    onUpdated: (field) => {
      onUpdate({
        ...task,
        form: {
          ...task.form,
          fields: task.form.fields.map((f) => {
            return f.id === field.id ? field : f;
          }),
        },
      });
      notifyNextWorkflows([true]);
    },
    onDeleted: () => {
      notifyNextWorkflows([true]);
    },
  });

  const fields = useMemo(() => {
    return (task?.form?.fields || []).map(mapFieldsAction);
  }, [task, mapFieldsAction]);

  return {
    fields: fields,
    isCreatingFormField: isCreatingFormField,
    isUpdatingForm: isUpdatingForm,
    isDeletingForm: isDeletingForm,
    createFormField: (taskId, field, placeBeforeItemId, formName) => {
      return createFormFieldMutation(
        {
          field: field,
          taskId: toGlobalId("Task", taskId),
          placeBeforeItemId: placeBeforeItemId,
          formName: formName,
        },
        {
          successMsg: "Form Field created successfully.",
          defaultErrorMsg: "Failed to create form field.",
        }
      );
    },
    updateForm: (fId, formName) => {
      return updateFormMutation(
        { id: fId, formName: formName },
        {
          successMsg: "Form updated successfully.",
          defaultErrorMsg: "Failed to update form.",
        }
      );
    },
    deleteForm: (fId) => {
      return deleteFormMutation(
        { formId: fId },
        {
          successMsg: "Form deleted successfully.",
          defaultErrorMsg: "Failed to delete form.",
        }
      );
    },
    updateFieldOrder: ({ destination: dst, source: src }) => {
      if (!dst || dst.index === src.index) return Promise.resolve(true);
      const beforeItem = src.index > dst.index ? dst.index : dst.index + 1;
      const orderedList = swap(fields, src.index, dst.index);

      onUpdate(
        { ...task, form: { ...task.form, fields: orderedList } },
        "edit",
        false
      );

      return updateFieldOrderMutation({
        targetId: fields[src.index].id,
        placeBeforeItemId: fields[beforeItem]?.id,
      }).catch((e) => {
        onUpdate({ ...task, form: { ...task.form, fields } }, "edit", false);

        fireError(
          Array.isArray(e) && e[0]?.message
            ? e[0].message
            : "Failed to reorder the form field, please retry or contact support for assistance",
          e
        );
      });
    },
  };
}

export default useTaskForm;
